import { ActionMenu, ButtonSecondary, Icon, type OptionProps } from '@amelio/ui-library';

import type { ThematicMeeting } from '@/types/thematic-meetings';

import { useGetThematicMeetingCalendarLinks } from '@/api/thematic-meetings';
import { useAmelioTranslations } from '@/hooks';
import { useMeetingCalendarDownload } from '@/pages/ThematicMeetings/hooks/useMeetingCalendarDownload';

const DOWNLOAD_ACTION = 'ics';

interface AddToCalendarMenuProps {
    meeting: ThematicMeeting;
}

/**
 * What an option with no address yet says about itself: that its link is still being prepared, or that
 * preparing it failed and the `.ics` file is the way out. Nothing once the link is there — the option
 * then opens the calendar and speaks for itself.
 */
const missingLinkNoticeKey = ({ isPending, isError }: { isPending: boolean; isError: boolean }) => {
    if (isPending) {
        return 'thematicMeetings.calendar.linksPending' as const;
    }
    if (isError) {
        return 'thematicMeetings.calendar.linksError' as const;
    }
    return undefined;
};

/**
 * The three ways to put the meeting in a calendar: opened prefilled in Outlook or in Google, in a new
 * tab, or downloaded as an `.ics` file for a calendar that takes no link. All three describe the one
 * event the server assembles from the meeting — the same title, and a length its agenda decides — so
 * whichever the manager picks, the employee is invited to the same thing. A meeting without a date opens
 * in Outlook and Google with no times, on a slot the manager picks there; the file cannot go without one,
 * so it carries the default slot, which the manager moves in their calendar.
 *
 * The links are fetched with the page, so each option is already a real link when the menu opens: a
 * middle click opens it, and its address can be copied. While they are on their way, and if they never
 * arrive, the option says which of the two it is: a link being prepared must not read as one that is
 * gone. The file is fetched on demand, and stays on offer when the links failed — it is the way out the
 * failure itself points to.
 */
export const AddToCalendarMenu = ({ meeting }: AddToCalendarMenuProps) => {
    const { t } = useAmelioTranslations();
    const {
        data: links,
        isPending,
        isError,
    } = useGetThematicMeetingCalendarLinks({ id: meeting.id });
    const { downloadCalendar, isDownloading } = useMeetingCalendarDownload();
    const noticeKey = missingLinkNoticeKey({ isPending, isError });

    const calendarOption = (id: string, label: string, url: string | undefined): OptionProps => ({
        id,
        label,
        startIcon: (
            <Icon
                name={'arrow-export'}
                size={'sm'}
                ariaHidden
            />
        ),
        disabled: !url,
        description: noticeKey && t(noticeKey),
        href: url,
        target: '_blank',
        dataTestId: `thematic-meeting-add-to-${id}`,
    });

    return (
        <ActionMenu
            options={[
                calendarOption('outlook', t('thematicMeetings.plan.addToOutlook'), links?.outlook),
                calendarOption('google', t('thematicMeetings.plan.addToGoogle'), links?.google),
                {
                    id: DOWNLOAD_ACTION,
                    label: t('thematicMeetings.plan.downloadInvitation'),
                    startIcon: (
                        <Icon
                            name={'arrow-import'}
                            size={'sm'}
                            ariaHidden
                        />
                    ),
                    disabled: isDownloading,
                    dataTestId: 'thematic-meeting-add-to-ics',
                },
            ]}
            onItemClick={(itemId) => {
                if (itemId === DOWNLOAD_ACTION) {
                    void downloadCalendar(meeting);
                }
            }}
            placement={'bottom-left'}
            menuAriaLabel={t('thematicMeetings.plan.addToCalendar')}
            dataTestId={'thematic-meeting-add-to-calendar'}
            renderTrigger={(trigger) => (
                <ButtonSecondary
                    {...trigger}
                    size={'md'}
                    className={'text-primary-800 px-1'}
                    leftIcon={
                        <Icon
                            name={'time-calendar-add'}
                            size={'sm'}
                            color={'inherit'}
                            ariaHidden
                        />
                    }
                    rightIcon={
                        <Icon
                            name={'arrow-arrow-down'}
                            size={'sm'}
                            color={'inherit'}
                            ariaHidden
                        />
                    }
                    dataTestId={'thematic-meeting-add-to-calendar-button'}
                >
                    {t('thematicMeetings.plan.addToCalendar')}
                </ButtonSecondary>
            )}
        />
    );
};
